import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import FeatureProtectedRoute from './components/FeatureProtectedRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import { TutorialProvider } from './context/TutorialContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeGenerator from './pages/KnowledgeGenerator';
import Transcripts from './pages/Transcripts';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import UploadSessions from './pages/UploadSessions';
import Subscription from './pages/Subscription';
import SupportTickets from './pages/SupportTickets';
import Calendar from './pages/Calendar';
import Training from './pages/Training';
import TrainingTracks from './pages/TrainingTracks';
import TrainingTrackBuilder from './pages/TrainingTrackBuilder';
import TrainingSessionEvaluation from './pages/TrainingSessionEvaluation';
import BulkUpload from './pages/BulkUpload';
import { store } from './store';
import { useRoleAccess } from './hooks/useRoleAccess';

/** Redirects to /knowledge-base for super admins, /dashboard for everyone else */
const DefaultRedirect: React.FC = () => {
  const { isSuperAdmin } = useRoleAccess();
  return <Navigate to={isSuperAdmin ? '/knowledge-base' : '/dashboard'} replace />;
};

// Lushin brand theme — values mirror styles/lushin-tokens.css.
const theme = createTheme({
  palette: {
    primary: {
      main: '#0A0B0F', // obsidian
    },
    secondary: {
      main: '#00E5C7', // plasma-cyan
    },
  },
  typography: {
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
    h1: { fontFamily: "'Space Grotesk', sans-serif" },
    h2: { fontFamily: "'Space Grotesk', sans-serif" },
    h3: { fontFamily: "'Space Grotesk', sans-serif" },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Router>
            <TutorialProvider>
            <Routes>
              <Route path="/" element={<DefaultRedirect />} />
              <Route 
                path="/login" 
                element={
                  <UnprotectedRoute>
                    <Login />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/signup" 
                element={
                  <UnprotectedRoute>
                    <Signup />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/knowledge-base"
                element={
                  <ProtectedRoute>
                    {/* Reachable via EITHER capability: the page hosts the old
                        objection/FAQ/landmine model and the live discovery
                        checklist, and Lushin ships the checklist without the
                        rest. The page itself hides the tabs that don't apply. */}
                    <FeatureProtectedRoute feature={['liveIntelligence', 'livePlaybook']}>
                      <RoleProtectedRoute requiredRole="admin">
                        <KnowledgeBase />
                      </RoleProtectedRoute>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/knowledge-generator"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="liveIntelligence">
                      <RoleProtectedRoute requiredRole="admin">
                        <KnowledgeGenerator />
                      </RoleProtectedRoute>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-upload"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="liveIntelligence">
                      <RoleProtectedRoute requiredRole="admin">
                        <BulkUpload />
                      </RoleProtectedRoute>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transcripts" 
                element={
                  <ProtectedRoute>
                    <Transcripts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Users />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions"
                element={
                  <ProtectedRoute>
                    <Sessions />
                  </ProtectedRoute>
                }
              />
              {/* Must precede /sessions/:sessionId so "upload" isn't treated as an id */}
              <Route
                path="/sessions/upload"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <UploadSessions />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <SessionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Subscription />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support-tickets"
                element={
                  <ProtectedRoute>
                    <SupportTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="training">
                      <Training />
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="training">
                      <RoleProtectedRoute requiredRole="admin">
                        <TrainingTrackBuilder />
                      </RoleProtectedRoute>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder/:trackId"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="training">
                      <RoleProtectedRoute requiredRole="admin">
                        <TrainingTrackBuilder />
                      </RoleProtectedRoute>
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <FeatureProtectedRoute feature="training">
                      <TrainingSessionEvaluation />
                    </FeatureProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route path="/dictionary" element={<Navigate to="/knowledge-base" replace />} />
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
            </TutorialProvider>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
